// Generated by GitHub Copilot
import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import { ImportWizard } from '#components/composites/import/import-wizard.tsx'
import { PermissionGuard, type PermissionRequirement } from '#components/tools/permission-guard.tsx'
import ImportApi from '#core/api/shared/ImportApi.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readGatePairs } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { useUserState } from '#core/state/UserState.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/data-import')({
  component: RouteComponent,
})

/** One entity's importer tab: which backend collection it drives, whose strings it renders, and where leaving it lands. */
interface ImportTab {
  /** REST collection leaf (`operations/<path>`), matching the backend import router prefix. Also the tab's identity and the entity key its importer strings sit under. */
  path: string
  /** The entity's list route, which the wizard's exit navigates to when the user may read it (`$companyId` filled in below). */
  listRoute: string
  /** i18n key of the tab label, in the `settings` namespace. */
  labelKey: string
  /** Service whose IMPORT permission gates the tab. */
  service: PermissionService
}

/** Every importer, in tab order — the one list to extend when a new entity gains an importer. */
const IMPORT_TABS: ImportTab[] = [
  { path: 'assets', listRoute: '/$companyId/assets', labelKey: 'nav.assets', service: PermissionService.ASSETS },
  { path: 'work-orders', listRoute: '/$companyId/work-orders', labelKey: 'nav.workOrders', service: PermissionService.WORK_ORDERS },
  { path: 'parts', listRoute: '/$companyId/parts', labelKey: 'nav.parts', service: PermissionService.PARTS },
  { path: 'periodic-maintenances', listRoute: '/$companyId/periodic-maintenances', labelKey: 'dataImportSettings.tabPeriodicMaintenances', service: PermissionService.PERIODIC_MAINTENANCES },
  { path: 'meters', listRoute: '/$companyId/meters', labelKey: 'nav.meters', service: PermissionService.METERS },
  { path: 'vendors', listRoute: '/$companyId/vendors', labelKey: 'nav.vendors', service: PermissionService.VENDORS },
]

/** IMPORT on any importer's service — the gate for this page, exported so the settings
 *  sidebar link to it is built from the same tab table instead of its own copy. */
export const IMPORT_TAB_PERMISSIONS: PermissionRequirement[][] = IMPORT_TABS.map((tab) => [
  [tab.service, PermissionAction.IMPORT],
])

/** Tabbed Data Import settings page — one importer per entity, filtered by IMPORT permission. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { companyId } = Route.useParams()
  const navigate = useNavigate()
  const userState = useUserState()

  const permittedTabs = IMPORT_TABS.filter((tab) => userState.hasPermission(tab.service, PermissionAction.IMPORT))

  const [selectedPath, setSelectedPath] = useState<string | undefined>(() => permittedTabs[0]?.path)
  // Clamp to a currently-permitted tab so an in-place company/permission change can't leave a
  // hidden or non-permitted tab active.
  const activeTab = permittedTabs.find((tab) => tab.path === selectedPath) ?? permittedTabs[0]
  const activePath = activeTab?.path
  // Build the active tab's client (stable per tab; the wizard remounts on tab switch).
  const importer = useMemo(() => (activePath ? new ImportApi(`operations/${activePath}`) : null), [activePath])

  return (
    <PermissionGuard permissions={IMPORT_TAB_PERMISSIONS} redirectOnNotAuthorized>
      <div className="p-4 overflow-y-auto h-100">
        <div className="mb-4">
          <h1 className="h4 fw-bold mb-1">{t('dataImportSettings.title')}</h1>
          <p className="text-secondary mb-0">{t('dataImportSettings.description')}</p>
        </div>

        <Tabs
          tabs={permittedTabs.map((tab) => ({ key: tab.path, label: t(tab.labelKey) }))}
          activeKey={activePath ?? ''}
          onChange={(key) => setSelectedPath(key)}
          variant="underline"
          size="sm"
          className="mb-4"
        />

        {activeTab && importer && (
          <ImportWizard
            key={activeTab.path}
            entity={activeTab.path}
            importer={importer}
            // IMPORT is independent of the read gate while every list route is read-gated, so
            // sending an import-only user there would land them on Not Authorized.
            onExit={
              userState.hasAnyPermission(readGatePairs(activeTab.service))
                ? () => void navigate({ to: activeTab.listRoute, params: { companyId } })
                : undefined
            }
          />
        )}
      </div>
    </PermissionGuard>
  )
}
